import React, { useState, useMemo } from "react";
import UndoToast from "./UndoToast";
import { CheckIcon, XIcon, ChevronIcon } from "../icons";
import { fmtDateFull } from "../utils/helpers";
import S from "../styles";

const JOB_TYPE_ORDER = ["Laser Cutting", "3D Printing", "UV Printing", "CNC"];
const JOB_TYPE_COLORS = { "Laser Cutting": "#ef4444", "3D Printing": "#8b5cf6", "UV Printing": "#3b82f6", CNC: "#f59e0b" };

export default function InProgressPage({ progressJobs, onApprove, onCancel, onOpenJob }) {
  const [toast, setToast] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const totalRevenue = progressJobs.reduce((s, j) => s + j.customerCost, 0);
  const totalCost = progressJobs.reduce((s, j) => s + j.ourCost, 0);
  const totalProfit = progressJobs.reduce((s, j) => s + j.profit, 0);
  const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  const grouped = useMemo(() => {
    const map = {};
    JOB_TYPE_ORDER.forEach((t) => (map[t] = []));
    map["Other"] = [];
    progressJobs.forEach((j) => {
      const t = j.jobType && JOB_TYPE_ORDER.includes(j.jobType) ? j.jobType : "Other";
      map[t].push(j);
    });
    // Return only groups that have jobs, in order
    return [...JOB_TYPE_ORDER, "Other"].filter((t) => map[t].length > 0).map((t) => ({ type: t, jobs: map[t] }));
  }, [progressJobs]);

  const toggle = (t) => setCollapsed((c) => ({ ...c, [t]: !c[t] }));

  const handleCancel = (job) => {
    onCancel(job.id);
    setToast({ job, key: Date.now() });
  };

  const handleUndo = () => {
    if (toast) {
      onApprove(null, toast.job);
      setToast(null);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Open Jobs</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track and manage jobs currently in progress</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 28 }}>
        <div style={S.statCard}><span style={S.statLabel}>Open Jobs</span><span style={S.statValue}>{progressJobs.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Pipeline Value</span><span style={S.statValue}>${totalRevenue.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Costs</span><span style={S.statValue}>${totalCost.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Expected Profit</span><span style={{ ...S.statValue, color: "#34d399" }}>${totalProfit.toLocaleString()}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Avg Margin</span><span style={S.statValue}>{avgMargin.toFixed(1)}%</span></div>
      </div>

      {grouped.map(({ type, jobs: gJobs }) => {
        const isOpen = collapsed[type] !== true;
        const color = JOB_TYPE_COLORS[type] || "rgba(10,10,15,0.58)";
        const groupRev = gJobs.reduce((s, j) => s + j.customerCost, 0);

        return (
          <div key={type} style={{ marginBottom: 16 }}>
            <button onClick={() => toggle(type)} style={{ ...S.monthHeader, borderColor: "#0A0A0F" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 4, height: 24, borderRadius: 0, background: color }} />
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>{type}</span>
                <span style={S.jobCountBadge}>{gJobs.length} job{gJobs.length !== 1 ? "s" : ""}</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Value</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 600, color: "#0033FF" }}>${groupRev.toLocaleString()}</div>
              </div>
            </button>
            {isOpen && (
              <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "14%" }}>Job #</th>
                      <th style={{ ...S.th, width: "14%" }}>Date Opened</th>
                      <th style={{ ...S.th, width: "24%" }}>Customer</th>
                      <th style={{ ...S.th, width: "16%", textAlign: "right" }}>Customer Cost</th>
                      <th style={{ ...S.th, width: "14%", textAlign: "right" }}>Our Cost</th>
                      <th style={{ ...S.th, width: "18%", textAlign: "right" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gJobs.map((j) => {
                      const margin = j.customerCost > 0 ? (j.profit / j.customerCost) * 100 : 0;
                      return (
                        <tr key={j.id}>
                          <td style={S.td}>
                            <button onClick={() => onOpenJob(j)} style={S.jobLink}>{j.jobNumber}</button>
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "rgba(10,10,15,0.58)" }}>
                            {fmtDateFull(j.dateOpened)}
                          </td>
                          <td style={{ ...S.td, fontWeight: 500, color: "#0A0A0F" }}>{j.customerName}</td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>
                            ${j.customerCost.toLocaleString()}
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>
                            ${j.ourCost.toLocaleString()}
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                              <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "2px 8px", borderRadius: 0}}>
                                {margin.toFixed(0)}%
                              </span>
                              <button onClick={() => onApprove(j.id)} style={S.approveBtn} title="Approve"><CheckIcon /></button>
                              <button onClick={() => handleCancel(j)} style={S.cancelBtn} title="Cancel"><XIcon /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {progressJobs.length === 0 && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
          No jobs in progress.
        </div>
      )}

      {toast && (
        <UndoToast
          key={toast.key}
          message={`"${toast.job.jobNumber}" cancelled`}
          onUndo={handleUndo}
          onExpire={() => setToast(null)}
        />
      )}
    </div>
  );
}
